import bizAiService from '@/modules/bizai/services/BizAiService';
import { i18n } from '@/plugins/i18n';
import {
  PULSE_VIEW,
  PULSE_CATEGORY,
  PULSE_CATEGORY_ORDER,
  PULSE_PAGE_LIMIT,
  PULSE_MAX_PAGE_LIMIT,
  PULSE_STATUS,
  PULSE_OUTCOME,
} from '@/modules/pulse/utils/pulseConstants';

const emptyCounts = () => ({
  [PULSE_CATEGORY.NEEDS_ATTENTION]: 0,
  [PULSE_CATEGORY.UPCOMING]: 0,
  [PULSE_CATEGORY.FYI]: 0,
});

const emptyItems = () => ({
  [PULSE_CATEGORY.NEEDS_ATTENTION]: [],
  [PULSE_CATEGORY.UPCOMING]: [],
  [PULSE_CATEGORY.FYI]: [],
});

const emptyPages = () => ({
  [PULSE_CATEGORY.NEEDS_ATTENTION]: { page: 1, hasMore: false },
  [PULSE_CATEGORY.UPCOMING]: { page: 1, hasMore: false },
  [PULSE_CATEGORY.FYI]: { page: 1, hasMore: false },
});

const state = {
  view: PULSE_VIEW.MY,
  selectedCategory: PULSE_CATEGORY.NEEDS_ATTENTION,
  itemsByCategory: emptyItems(),
  counts: emptyCounts(),
  pageByCategory: emptyPages(),
  loading: false,
  error: null,
  pendingUids: [],
  outcomeByUid: {},
  // Monotonic counter bumped by a poll that changed the list; drives the banner.
  refreshToken: 0,
};

const getters = {
  view: (s) => s.view,
  selectedCategory: (s) => s.selectedCategory,
  counts: (s) => s.counts,
  totalActive: (s) => Object.values(s.counts).reduce((sum, count) => sum + count, 0),
  itemsByCategory: (s) => (category) => s.itemsByCategory[category] || [],
  pageByCategory: (s) => (category) => s.pageByCategory[category] || { page: 1, hasMore: false },
  selectedItems: (s) => s.itemsByCategory[s.selectedCategory] || [],
  // Groups the selected category's items by action_group, preserving first-seen order.
  selectedGroups: (s, g) => {
    const groups = [];
    const indexByKey = new Map();
    g.selectedItems.forEach((item) => {
      const key = item.action_group || '';
      if (!indexByKey.has(key)) {
        indexByKey.set(key, groups.length);
        groups.push({ key, label: item.action_group || null, items: [] });
      }
      groups[indexByKey.get(key)].items.push(item);
    });
    return groups;
  },
  selectedPage: (s) => s.pageByCategory[s.selectedCategory] || { page: 1, hasMore: false },
  isLoading: (s) => s.loading,
  error: (s) => s.error,
  isSelectedEmpty: (s) => (s.itemsByCategory[s.selectedCategory] || []).length === 0,
  isEmpty: (s, g) => g.totalActive === 0,
  isPending: (s) => (uid) => s.pendingUids.includes(uid),
  itemOutcome: (s) => (uid) => s.outcomeByUid[uid]?.outcome || null,
  refreshToken: (s) => s.refreshToken,
  // True while a write/undo is mid-flight, so a poll can skip and not clobber it.
  isFeedBusy: (s) => s.pendingUids.length > 0 || Object.keys(s.outcomeByUid).length > 0,
  itemsByContext: (s) => (contextUid) => PULSE_CATEGORY_ORDER
    .flatMap((category) => s.itemsByCategory[category])
    .filter((item) => item?.context?.context_uid === contextUid),
};

const mutations = {
  setView(state, view) {
    state.view = view;
  },
  setSelectedCategory(state, category) {
    state.selectedCategory = category;
  },
  setCategory(state, { category, items, page }) {
    state.itemsByCategory = { ...state.itemsByCategory, [category]: items };
    state.pageByCategory = { ...state.pageByCategory, [category]: page };
  },
  appendCategory(state, { category, items, page }) {
    state.itemsByCategory = {
      ...state.itemsByCategory,
      [category]: [...state.itemsByCategory[category], ...items],
    };
    state.pageByCategory = { ...state.pageByCategory, [category]: page };
  },
  setCounts(state, counts) {
    state.counts = {
      [PULSE_CATEGORY.NEEDS_ATTENTION]: counts[PULSE_CATEGORY.NEEDS_ATTENTION] || 0,
      [PULSE_CATEGORY.UPCOMING]: counts[PULSE_CATEGORY.UPCOMING] || 0,
      [PULSE_CATEGORY.FYI]: counts[PULSE_CATEGORY.FYI] || 0,
    };
  },
  removeItem(state, { category, uid }) {
    state.itemsByCategory = {
      ...state.itemsByCategory,
      [category]: state.itemsByCategory[category].filter((item) => item.uid !== uid),
    };
    if (state.counts[category] > 0) {
      state.counts = { ...state.counts, [category]: state.counts[category] - 1 };
    }
  },
  setLoading(state, loading) {
    state.loading = loading;
  },
  setError(state, error) {
    state.error = error;
  },
  addPending(state, uid) {
    if (!state.pendingUids.includes(uid)) state.pendingUids = [...state.pendingUids, uid];
  },
  removePending(state, uid) {
    state.pendingUids = state.pendingUids.filter((pendingUid) => pendingUid !== uid);
  },
  setOutcome(state, { uid, outcome, targetState }) {
    state.outcomeByUid = { ...state.outcomeByUid, [uid]: { outcome, targetState } };
  },
  clearOutcome(state, uid) {
    const next = { ...state.outcomeByUid };
    delete next[uid];
    state.outcomeByUid = next;
  },
  bumpRefreshToken(state) {
    state.refreshToken += 1;
  },
};

const findCategoryOfItem = (s, uid) => PULSE_CATEGORY_ORDER.find(
  (category) => s.itemsByCategory[category].some((item) => item.uid === uid),
);

// Order-sensitive fingerprint of a category's list + count, to detect poll changes.
const feedSignature = (s, category) => {
  const uids = (s.itemsByCategory[category] || []).map((item) => item.uid).join(',');
  return `${s.counts[category] || 0}|${uids}`;
};

const actions = {
  async setView({ commit, dispatch }, view) {
    commit('setView', view);
    await dispatch('fetchFeed');
  },
  // Switching the active category triggers a fresh fetch for that category only.
  async selectCategory({ commit, dispatch, state: s }, category) {
    if (category === s.selectedCategory) return;
    commit('setSelectedCategory', category);
    await dispatch('fetchFeed');
  },
  // Fetches only the currently selected category. Counts for every category are
  // returned alongside any single-category response, so the tabs stay accurate.
  // `silent` skips the skeleton/error-blanking and returns whether it succeeded.
  async fetchFeed({ commit, state: s }, { silent = false } = {}) {
    const { selectedCategory } = s;
    if (!silent) {
      commit('setLoading', true);
      commit('setError', null);
    }
    // Silent refreshes (polling, post-action reconcile) re-fetch the span the
    // user already loaded via "Show more" so they don't collapse back to page 1.
    // Clamped to the API's per_page cap; the page cursor follows what was
    // actually requested so "Show more" stays consistent past the cap.
    const loaded = s.itemsByCategory?.[selectedCategory]?.length || 0;
    const spanPages = silent && loaded > PULSE_PAGE_LIMIT
      ? Math.ceil(loaded / PULSE_PAGE_LIMIT)
      : 1;
    const perPage = Math.min(spanPages * PULSE_PAGE_LIMIT, PULSE_MAX_PAGE_LIMIT);
    const page = Math.floor(perPage / PULSE_PAGE_LIMIT);
    try {
      const response = await bizAiService.getRecommendations({
        category: selectedCategory,
        per_page: perPage,
        include: 'counts',
        producer: 'any',
      });
      const items = response?.airecommendations || [];
      const counts = response?.meta?.counts || {};
      commit('setCategory', {
        category: selectedCategory,
        items,
        page: { page, hasMore: items.length < (counts[selectedCategory] || 0) },
      });
      commit('setCounts', counts);
      // Success clears any prior error so a recovered silent poll drops it.
      commit('setError', null);
      return true;
    } catch (error) {
      if (!silent) commit('setError', error);
      console.error('Failed to fetch Pulse feed', error);
      return false;
    } finally {
      if (!silent) commit('setLoading', false);
    }
  },
  // Background poll: silent re-fetch; bump the banner token only if the list changed.
  async pollFeed({ commit, dispatch, getters: g, state: s }) {
    if (g.isFeedBusy) return;
    const { selectedCategory } = s;
    const before = feedSignature(s, selectedCategory);
    const ok = await dispatch('fetchFeed', { silent: true });
    if (!ok) return;
    if (feedSignature(s, selectedCategory) !== before) {
      commit('bumpRefreshToken');
    }
  },
  async loadMore({ commit, state: s }, category) {
    const page = s.pageByCategory[category];
    if (!page?.hasMore) return;
    try {
      const nextPage = (page.page || 1) + 1;
      const response = await bizAiService.getRecommendations({
        category,
        per_page: PULSE_PAGE_LIMIT,
        include: 'counts',
        page: nextPage,
        producer: 'any',
      });
      const items = response?.airecommendations || [];
      const counts = response?.meta?.counts || {};
      const loaded = (s.itemsByCategory[category]?.length || 0) + items.length;
      commit('appendCategory', {
        category,
        items,
        page: { page: nextPage, hasMore: loaded < (counts[category] || loaded) },
      });
      commit('setCounts', counts);
    } catch (error) {
      console.error('Failed to load more Pulse items', error);
    }
  },
  // Secondary actions are debounced: they only show the confirmation and record
  // the intended state. The recommendation is written once the undo window
  // closes (finalizeOutcome), so Undo cancels it without any request being sent
  // — this avoids a dismiss/complete PUT racing an undo "active" PUT.
  markCompleted({ commit }, uid) {
    commit('setOutcome', { uid, outcome: PULSE_OUTCOME.DONE, targetState: PULSE_STATUS.COMPLETED });
  },
  dismissItem({ commit }, uid) {
    commit('setOutcome', { uid, outcome: PULSE_OUTCOME.REMOVED, targetState: PULSE_STATUS.DISMISSED });
  },
  // The confirmation window elapsed without an undo — commit the change and drop
  // the item. This is the single, debounced write for the secondary action. The
  // confirmation tile stays up and swaps Undo for "Working on it..." while the
  // request is in flight (addPending with the outcome still set).
  async finalizeOutcome({ commit, dispatch, state: s }, uid) {
    const entry = s.outcomeByUid[uid];
    if (!entry) return;
    const category = findCategoryOfItem(s, uid);
    commit('addPending', uid);
    try {
      await bizAiService.updateRecommendation(uid, entry.targetState);
      // Optimistic remove for instant feedback, then silent re-fetch to reconcile.
      if (category) commit('removeItem', { category, uid });
      dispatch('fetchFeed', { silent: true });
    } catch (error) {
      // Surface a toast rather than the feed-level error: a single item's write
      // failure must not blank the whole feed. The item stays and can be retried.
      dispatch('ToastStore/setToastConfig', {
        value: true,
        type: 'attention',
        bodyText: i18n.t('pulse.item.outcome.action_failed'),
        timeout: 4000,
      }, { root: true });
      console.error(`Failed to update Pulse item (${entry.targetState})`, error);
    } finally {
      commit('clearOutcome', uid);
      commit('removePending', uid);
    }
  },
  // Undo before the window closes: nothing was written, so just drop the
  // confirmation and leave the recommendation untouched (still active).
  undoOutcome({ commit }, uid) {
    commit('clearOutcome', uid);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
